import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, FileText, Image as ImageIcon, MapPin, Plus, Save, Store,
    Trash2, Upload, X,
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import SafeImage from '../../components/SafeImage';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useOptions } from '../../hooks/useOptions';
import { useLocationLists, lookupPin } from '../../utils/locationApi';
import { initialFor, mediaUrl } from './vendorShared';
import { RULES, checkFiles, limitNote, uploadFailMessage } from '../../utils/uploadRules';
import '../Profiles/Profiles.css';

// Every field here maps to a Service Provider field on the User model (see
// server/models/User.js) — approval/active-state/login fields stay on their
// own dedicated actions (ProviderDetail.jsx), not this general edit form.
const blankForm = {
    businessName: '', ownerName: '', category: '', mobile: '', email: '',
    addressLine1: '', addressLine2: '', city: '', district: '', state: '', country: 'India', pincode: '',
    description: '',
};

const VendorEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    // Staged, not-yet-uploaded gallery selections — accumulates across
    // repeated single picks AND multi-selects (never replaces what's
    // already staged), shows a local preview with its own remove button,
    // and only hits the server once "Upload" is clicked.
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);

    useEffect(() => {
        const urls = galleryFiles.map(f => URL.createObjectURL(f));
        setGalleryPreviews(urls);
        return () => urls.forEach(u => URL.revokeObjectURL(u));
    }, [galleryFiles]);

    const fetchVendor = useCallback(async () => {
        try {
            const res = await API.get('/admin/vendors');
            const found = (res.data.vendors || []).find(v => v._id === id);
            if (!found) {
                setNotFound(true);
                return;
            }
            setVendor(found);
            setForm({
                businessName: found.businessName || '', ownerName: found.ownerName || '',
                category: found.category || '', mobile: found.mobile || '', email: found.email || '',
                addressLine1: found.addressLine1 || '', addressLine2: found.addressLine2 || '',
                city: found.city || '', district: found.district || '', state: found.state || '',
                country: found.country || 'India', pincode: found.pincode || '',
                description: found.description || '',
            });
        } catch (err) {
            toast.error('Failed to load service provider');
        }
    }, [id]);

    useEffect(() => { fetchVendor(); }, [fetchVendor]);

    // Same admin-managed taxonomy the member-facing app and Service
    // Categories page already use, so admin edits stay consistent with
    // what providers pick from at registration.
    const { options: categoryOptions } = useOptions('servicecategory');
    // the managed categories, plus the provider's current one when it is not in that list (so it never shows blank)
    const categoryList = [...new Set([form.category, ...categoryOptions.map(option => (typeof option === 'string' ? option : option.value))].filter(Boolean))];
    // country, state and district come from the same location lists as every other form
    const { countries, states: stateOptions, districts: districtOptions } = useLocationLists(form.country, form.state, form.district);

    const set = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
    const onChange = e => set(e.target.name, e.target.value);

    // Typing a full 6-digit Indian PIN code fills country, state, district and city
    const [pinNote, setPinNote] = useState('');
    const isIndia = String(form.country || 'India').trim().toLowerCase() === 'india';
    const changePin = (raw) => {
        const pin = isIndia ? raw.replace(/\D/g, '').slice(0, 6) : raw.slice(0, 12);
        set('pincode', pin);
        setPinNote('');
        if (!isIndia || pin.length !== 6) return;
        setPinNote('Finding the place…');
        lookupPin(pin).then(place => {
            if (!place) { setPinNote('PIN code not found. Choose the place yourself.'); return; }
            const district = place.districtOther || place.district;
            setForm(prev => (prev.pincode === pin ? { ...prev, country: place.country, state: place.state, district, city: place.city } : prev));
            setPinNote(`Filled from PIN code: ${[place.city, district, place.state].filter(Boolean).join(', ')}`);
        });
    };

    const handleSave = async e => {
        e.preventDefault();
        if (!form.category) { toast.error('Choose a service category'); return; }
        if (!/^\d{10}$/.test(form.mobile)) { toast.error('Enter a valid 10-digit mobile number'); return; }
        if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) { toast.error('Enter a valid email address, or leave it empty'); return; }
        setSaving(true);
        try {
            // Any gallery photos picked but not yet uploaded via the
            // Gallery section's own "Upload" button ride along here too —
            // otherwise a Save Changes click looks like it worked (it did,
            // for the text fields) while silently dropping the photos.
            if (galleryFiles.length > 0) {
                const photoData = new FormData();
                galleryFiles.forEach(f => photoData.append('photos', f));
                await API.post(`/admin/vendors/${id}/photos`, photoData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setGalleryFiles([]);
            }
            await API.put(`/admin/vendors/${id}`, form);
            toast.success('Service provider updated!');
            navigate(`/service-providers/${id}`);
        } catch (err) {
            toast.error(galleryFiles.length && (err.response?.status === 413 || !err.response) ? uploadFailMessage(err, galleryFiles, RULES.photo) : (err.response?.data?.message || 'Failed to update service provider'));
        } finally {
            setSaving(false);
        }
    };

    // Logo acts immediately (not bundled into the Save button) — same pattern
    // as profile photo uploads on ProfileEdit.jsx, since it's a separate
    // multipart endpoint, not part of `form`.
    const handleUploadLogo = async file => {
        if (!file) return;
        if (!checkFiles([file], RULES.photo).length) return;
        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('logo', file);
        try {
            const res = await API.post(`/admin/vendors/${id}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Logo updated!');
            setVendor(res.data.vendor);
        } catch (err) {
            toast.error(uploadFailMessage(err, [file], RULES.photo));
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleRemoveLogo = async () => {
        try {
            const res = await API.delete(`/admin/vendors/${id}/logo`);
            toast.success('Logo removed');
            setVendor(res.data.vendor);
        } catch (err) {
            toast.error('Failed to remove logo');
        }
    };

    // Picking files only stages a local preview — nothing uploads until
    // "Upload" is clicked. Adds to whatever's already staged rather than
    // replacing it, so both "pick one, then pick another" and "select
    // several at once" behave the same way.
    const handleStageGalleryPhotos = files => {
        if (!Array.from(files || []).length) return;
        const accepted = checkFiles(files, RULES.photo, { have: (vendor.photos || []).length + galleryFiles.length, maxFiles: 15 });
        if (accepted.length) setGalleryFiles(prev => [...prev, ...accepted]);
    };

    const removeStagedGalleryPhoto = index => {
        setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Uploads everything staged, in one request.
    const handleUploadGallery = async () => {
        if (!galleryFiles.length) return;
        setUploadingGallery(true);
        const formData = new FormData();
        galleryFiles.forEach(f => formData.append('photos', f));
        try {
            const res = await API.post(`/admin/vendors/${id}/photos`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(res.data.message || 'Photo(s) uploaded!');
            setVendor(res.data.vendor);
            setGalleryFiles([]);
        } catch (err) {
            toast.error(uploadFailMessage(err, galleryFiles, RULES.photo));
        } finally {
            setUploadingGallery(false);
        }
    };

    const handleRemoveGalleryPhoto = async photoUrl => {
        try {
            const filename = photoUrl.split('/').pop();
            const res = await API.delete(`/admin/vendors/${id}/photos/${filename}`);
            toast.success('Photo removed');
            setVendor(res.data.vendor);
        } catch (err) {
            toast.error('Failed to remove photo');
        }
    };

    const backBtn = (
        <button type="button" className="profiles-btn" style={styles.backBtn} onClick={() => navigate(`/service-providers/${id}`)}>
            <ArrowLeft size={15} /> Back to Provider
        </button>
    );

    if (notFound) {
        return (
            <div style={styles.layout}>
                <Sidebar />
                <div className="admin-content" style={styles.content}>
                    <Navbar title={<span style={styles.navTitle}><Store size={18} /> Edit Service Provider</span>} />
                    <div style={styles.inner}>
                        <button type="button" className="profiles-btn" style={styles.backBtn} onClick={() => navigate('/services')}>
                            <ArrowLeft size={15} /> Back to Service Providers
                        </button>
                        <div style={styles.notFound}>
                            <Store size={40} color="var(--admin-text-muted)" />
                            <p>This service provider doesn't exist, or has been deleted.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!vendor) {
        return (
            <div style={styles.layout}>
                <Sidebar />
                <div className="admin-content" style={styles.content}>
                    <Navbar title={<span style={styles.navTitle}><Store size={18} /> Edit Service Provider</span>} />
                </div>
            </div>
        );
    }

    return (
        <div style={styles.layout}>
            <Sidebar />
            <div className="admin-content" style={styles.content}>
                <Navbar title={<span style={styles.navTitle}><Store size={18} /> Edit {vendor.businessName}</span>} />
                <form style={styles.inner} onSubmit={handleSave}>
                    {backBtn}

                    <div style={styles.sectionsGrid}>
                        <div className="profiles-card" style={{ ...styles.sectionCard, gridColumn: '1 / -1' }}>
                            <div style={styles.sectionTitle}><Store size={13} /> Logo</div>
                            <div style={styles.logoRow}>
                                {vendor.logo
                                    ? <SafeImage src={mediaUrl(vendor.logo)} alt={vendor.businessName} style={styles.logoImg} />
                                    : <span style={styles.logoFallbackInitials}>{initialFor(vendor.businessName)}</span>}
                                <div style={styles.logoActions}>
                                    <label className="profiles-btn" style={{ ...styles.uploadBtn, opacity: uploadingLogo ? 0.7 : 1 }}>
                                        {uploadingLogo ? 'Uploading...' : <><Upload size={13} /> {vendor.logo ? 'Replace' : 'Upload'}</>}
                                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingLogo}
                                            onChange={e => { handleUploadLogo(e.target.files[0]); e.target.value = ''; }} />
                                    </label>
                                    <small style={{ display: 'block', width: '100%', fontSize: 11, opacity: 0.7 }}><b>Limit:</b> {limitNote(RULES.photo)}</small>
                                    {vendor.logo && (
                                        <button type="button" className="profiles-btn" style={styles.removeBtn} onClick={handleRemoveLogo}>
                                            <Trash2 size={13} /> Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="profiles-card" style={styles.sectionCard}>
                            <div style={styles.sectionTitle}><Store size={13} /> Business</div>
                            <div style={styles.fieldGrid}>
                                <Field label="Business Name"><input className="profiles-input" style={styles.input} name="businessName" value={form.businessName} onChange={onChange} /></Field>
                                <Field label="Owner Name"><input className="profiles-input" style={styles.input} name="ownerName" value={form.ownerName} onChange={onChange} /></Field>
                                <Field label="Category">
                                    <Select value={form.category} onChange={v => set('category', v)} allowEmpty={false} placeholder="Select category" ariaLabel="Category"
                                        options={categoryList} />
                                </Field>
                                <Field label="Mobile Number">
                                    <input className="profiles-input" style={styles.input} name="mobile" value={form.mobile} inputMode="numeric" maxLength={10} placeholder="10-digit mobile number"
                                        onChange={e => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))} />
                                </Field>
                                <Field label="Email">
                                    <input className="profiles-input" style={styles.input} name="email" type="email" value={form.email} placeholder="name@example.com" onChange={onChange} />
                                </Field>
                                <div style={{ gridColumn: '1 / -1', color: 'var(--admin-text-muted)', fontSize: '12px' }}>
                                    Changing the mobile number or email makes it unverified. On the provider page, use "Send code" to verify the new one.
                                </div>
                            </div>
                        </div>

                        <div className="profiles-card" style={styles.sectionCard}>
                            <div style={styles.sectionTitle}><MapPin size={13} /> Location</div>
                            <div style={styles.fieldGrid}>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Address Line 1"><input className="profiles-input" style={styles.input} name="addressLine1" value={form.addressLine1} onChange={onChange} /></Field>
                                </div>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <Field label="Address Line 2"><input className="profiles-input" style={styles.input} name="addressLine2" value={form.addressLine2} onChange={onChange} /></Field>
                                </div>
                                <Field label="City"><input className="profiles-input" style={styles.input} name="city" value={form.city} onChange={onChange} /></Field>
                                <Field label="State">
                                    <Select value={form.state} options={stateOptions} disabled={!form.country} placeholder={form.country ? 'Select state' : 'Select country first'}
                                        onChange={v => setForm(prev => ({ ...prev, state: v, district: '' }))} />
                                </Field>
                                <Field label="District">
                                    <Select value={form.district} onChange={v => set('district', v)} options={districtOptions}
                                        disabled={!form.state} placeholder={form.state ? 'Select' : 'Select state first'} />
                                </Field>
                                <Field label="Country">
                                    <Select value={form.country} options={countries} allowEmpty={false}
                                        onChange={v => setForm(prev => ({ ...prev, country: v, state: '', district: '' }))} />
                                </Field>
                                <Field label={isIndia ? 'PIN Code' : 'Postal Code'}>
                                    <input className="profiles-input" style={styles.input} name="pincode" value={form.pincode} onChange={e => changePin(e.target.value)} inputMode={isIndia ? 'numeric' : 'text'} maxLength={12}
                                        placeholder={isIndia ? '6-digit PIN code' : 'Postal code'} />
                                    {pinNote && <small style={{ display: 'block', marginTop: '5px', color: 'var(--admin-text-muted)' }}>{pinNote}</small>}
                                </Field>
                            </div>
                        </div>

                        <div className="profiles-card" style={{ ...styles.sectionCard, gridColumn: '1 / -1' }}>
                            <div style={styles.sectionTitle}><FileText size={13} /> Description</div>
                            <textarea
                                className="profiles-input"
                                style={{ ...styles.input, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }}
                                name="description" value={form.description} onChange={onChange}
                                placeholder="A short description of the business"
                            />
                        </div>

                        <div className="profiles-card" style={{ ...styles.sectionCard, gridColumn: '1 / -1' }}>
                            <div style={styles.sectionTitle}><ImageIcon size={13} /> Gallery</div>
                            <div style={styles.galleryLabel}>{(vendor.photos || []).length}/15 photos</div>
                            <div style={styles.photoThumbGrid}>
                                {(vendor.photos || []).map((p, i) => (
                                    <div key={p} style={{ position: 'relative' }}>
                                        <SafeImage src={mediaUrl(p)} alt={`Gallery photo ${i + 1}`} style={styles.galleryThumb} />
                                        <button type="button" className="profiles-btn" style={styles.docRemoveBtn}
                                            onClick={() => handleRemoveGalleryPhoto(p)} aria-label="Remove photo">
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}

                                {/* Staged — picked but not yet uploaded */}
                                {galleryPreviews.map((url, i) => (
                                    <div key={url} style={{ position: 'relative' }}>
                                        <img src={url} alt="" style={{ ...styles.galleryThumb, border: '1.5px dashed var(--admin-primary)', opacity: uploadingGallery ? 0.5 : 1 }} />
                                        {!uploadingGallery && (
                                            <button type="button" className="profiles-btn" style={styles.docRemoveBtn}
                                                onClick={() => removeStagedGalleryPhoto(i)} aria-label="Remove">
                                                <X size={12} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {(vendor.photos || []).length + galleryFiles.length < 15 && (
                                    <label className="profiles-btn" style={styles.addGalleryBtn}>
                                        <Plus size={20} />
                                        <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                                            onChange={e => { handleStageGalleryPhotos(e.target.files); e.target.value = ''; }} />
                                    </label>
                                )}
                            </div>

                            {galleryFiles.length > 0 && (
                                <button type="button" className="profiles-btn" style={{ ...styles.uploadBtn, marginTop: '12px', opacity: uploadingGallery ? 0.7 : 1 }}
                                    disabled={uploadingGallery} onClick={handleUploadGallery}>
                                    <Upload size={13} /> {uploadingGallery ? 'Uploading...' : `Upload ${galleryFiles.length} photo${galleryFiles.length > 1 ? 's' : ''}`}
                                </button>
                            )}

                            <p style={styles.galleryHint}>
                                Pick one photo at a time or several at once — either way they're added to the list above. Limit: {limitNote(RULES.photo, { maxFiles: 15 })}.
                                Nothing uploads until you click "Upload".
                            </p>
                        </div>
                    </div>

                    <div style={styles.formActions}>
                        <button type="button" className="profiles-btn" style={styles.cancelBtn} onClick={() => navigate(`/service-providers/${id}`)}>Cancel</button>
                        <button type="submit" className="profiles-btn" style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }} disabled={saving}>
                            <Save size={15} /> {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <label style={styles.label}>{label}</label>
        {children}
    </div>
);

const styles = {
    layout: { display: 'flex', minHeight: '100vh', background: 'var(--admin-bg)' },
    content: { flex: 1 },
    inner: { padding: '28px', maxWidth: '1100px', margin: '0 auto' },
    navTitle: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
    backBtn: {
        display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '18px',
        padding: '8px 14px', background: 'var(--admin-surface)', color: 'var(--admin-text-secondary)',
        border: '1px solid var(--admin-border-strong)', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    },
    notFound: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
        padding: '80px 20px', color: 'var(--admin-text-secondary)',
    },
    sectionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' },
    sectionCard: {
        background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', boxShadow: 'var(--admin-shadow)',
        borderRadius: '12px', padding: '18px',
    },
    sectionTitle: {
        display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700',
        color: 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '14px', borderBottom: '1px solid var(--admin-border)', paddingBottom: '8px',
    },
    logoRow: { display: 'flex', alignItems: 'center', gap: '18px' },
    logoImg: { width: '76px', height: '76px', borderRadius: '16px', objectFit: 'cover', border: '1px solid var(--admin-border)' },
    logoFallbackInitials: {
        width: '76px', height: '76px', borderRadius: '16px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--admin-primary)', color: '#fff', fontSize: '26px', fontWeight: '700',
    },
    logoActions: { display: 'flex', gap: '8px' },
    uploadBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
        padding: '8px 14px', background: 'var(--admin-primary-soft)', color: 'var(--admin-primary)',
        border: 'none', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
    },
    removeBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
        padding: '8px 14px', background: 'none', color: '#B71C1C', border: '1.5px solid #B71C1C',
        borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
    },
    galleryLabel: { fontSize: '11px', fontWeight: '700', color: 'var(--admin-text-secondary)', textTransform: 'uppercase', marginBottom: '10px' },
    photoThumbGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
    galleryThumb: { width: '90px', height: '90px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--admin-border)' },
    docRemoveBtn: {
        position: 'absolute', top: '-6px', right: '-6px', width: '20px', height: '20px', borderRadius: '50%',
        background: '#B71C1C', color: '#fff', border: '2px solid var(--admin-surface)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
    },
    addGalleryBtn: {
        width: '90px', height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1.5px dashed var(--admin-border-strong)', borderRadius: '8px', color: 'var(--admin-text-secondary)',
        background: 'var(--admin-surface)', cursor: 'pointer',
    },
    addGalleryUploading: { fontSize: '12px', color: 'var(--admin-text-secondary)' },
    galleryHint: { fontSize: '11.5px', color: 'var(--admin-text-muted)', marginTop: '10px' },
    fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--admin-text-secondary)', marginBottom: '5px', textTransform: 'uppercase' },
    input: { width: '100%', padding: '9px 12px', border: '1.5px solid var(--admin-border-strong)', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--admin-surface)', color: 'var(--admin-text)' },
    formActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
    cancelBtn: { padding: '10px 20px', background: 'var(--admin-surface)', color: 'var(--admin-text-secondary)', border: '1.5px solid var(--admin-border-strong)', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    saveBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 24px', background: 'var(--admin-primary)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default VendorEdit;
